// src/components/compliance/DocumentsTab.tsx
//
// Compliance Documents (evidence) UX. IMPORTANT:
// Central Document Platform is unavailable today. Every `pending_upload`
// row surfaces a persistent banner explaining that upload is disabled.
// If a user does try to upload, the backend returns HTTP 503 with
// code='central_documents_unavailable' and we surface the exact copy.
// We NEVER silently no-op or route the file to FileAsset.

import { useMemo, useState } from 'react';
import {
  useGetComplianceDocumentsQuery,
  useCreateComplianceDocumentMutation,
  useSubmitComplianceDocumentMutation,
  useArchiveComplianceDocumentMutation,
  useRenewComplianceDocumentMutation,
  useUploadComplianceDocumentMutation,
  useGetComplianceRequirementsQuery,
} from '../../api/complianceApi';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorState from '../common/ErrorState';
import Button from '../common/Button';
import { DocumentStatusChip } from './Chips';
import {
  DOC_STATUS_LABEL,
  formatShortDate,
  messageFromComplianceError,
  SUBJECT_TYPE_LABEL,
} from './complianceDisplay';
import { useHasCapability } from '../../services/centralContext';
import type {
  ComplianceDocument,
  ComplianceDocumentStatus,
  ComplianceSubjectType,
} from '../../types/api';

interface DocumentsTabProps {
  workspaceId: string;
}

const STATUS_FILTERS: readonly ComplianceDocumentStatus[] = [
  'pending_upload',
  'draft',
  'pending_approval',
  'approved',
  'active',
  'expiring',
  'expired',
  'rejected',
  'archived',
];

export default function DocumentsTab({ workspaceId: _workspaceId }: DocumentsTabProps) {
  const canUpload = useHasCapability('compliance', 'upload');
  const [status, setStatus] = useState<ComplianceDocumentStatus | ''>('');
  const [subjectType, setSubjectType] = useState<ComplianceSubjectType | ''>('');
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<ComplianceDocument | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useGetComplianceDocumentsQuery({
    status: status || undefined,
    subjectType: subjectType || undefined,
    limit: 200,
  });

  // Workspace-wide pending_upload count. Independent of every filter
  // (status/subject/search/pagination) on this tab — the tab-level
  // banner needs the TRUE workspace count, not the filtered view.
  //
  // Backend contract: `getComplianceDocuments` uses `findAndCountAll`,
  // so `data.total` is the total match count for the query — sending
  // `limit: 1` gives us a lightweight query whose `.total` is the
  // authoritative workspace-wide `pending_upload` count.
  const { data: pendingUploadGlobal } = useGetComplianceDocumentsQuery({
    status: 'pending_upload',
    limit: 1,
  });
  const globalPendingUploadCount = pendingUploadGlobal?.total ?? 0;

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorState title="Could not load documents" message={messageFromComplianceError(error)} onRetry={refetch} />;

  const rows = data?.rows ?? [];

  return (
    <section className="space-y-4" data-testid="compliance-documents-tab">
      {globalPendingUploadCount > 0 && (
        <div
          role="alert"
          className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          data-testid="compliance-docs-upload-disabled-banner"
        >
          <strong>Upload is disabled.</strong>{' '}
          <span data-testid="compliance-docs-pending-upload-count">{globalPendingUploadCount}</span>
          {' '}document
          {globalPendingUploadCount === 1 ? '' : 's'} in this workspace {globalPendingUploadCount === 1 ? 'is' : 'are'} waiting for evidence. The Central Document Platform is not yet enabled — until then, evidence cannot be attached and these rows remain in <em>pending upload</em>.
        </div>
      )}

      {banner && (
        <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {banner}
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="doc-status" className="block text-xs font-medium text-gray-700 mb-1">Status</label>
            <select
              id="doc-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as ComplianceDocumentStatus | '')}
              className="rounded border-gray-300 text-sm"
              data-testid="compliance-docs-status-filter"
            >
              <option value="">All</option>
              {STATUS_FILTERS.map((s) => (
                <option key={s} value={s}>{DOC_STATUS_LABEL[s]}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="doc-subject" className="block text-xs font-medium text-gray-700 mb-1">Subject</label>
            <select
              id="doc-subject"
              value={subjectType}
              onChange={(e) => setSubjectType(e.target.value as ComplianceSubjectType | '')}
              className="rounded border-gray-300 text-sm"
              data-testid="compliance-docs-subject-filter"
            >
              <option value="">Any</option>
              {(Object.entries(SUBJECT_TYPE_LABEL) as Array<[ComplianceSubjectType, string]>).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
        </div>
        {canUpload && (
          <Button onClick={() => setCreating(true)} data-testid="compliance-doc-new-btn">
            New evidence entry
          </Button>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2">Title</th>
              <th className="px-4 py-2">Requirement</th>
              <th className="px-4 py-2">Subject</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Issued</th>
              <th className="px-4 py-2">Expires</th>
              <th className="px-4 py-2">Owner</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50" data-testid={`compliance-doc-row-${row.id}`}>
                <td className="px-4 py-2 text-gray-900 font-medium">{row.title}</td>
                <td className="px-4 py-2 text-gray-700">{row.requirement?.name ?? '—'}</td>
                <td className="px-4 py-2 text-gray-700 text-xs">
                  <div>{SUBJECT_TYPE_LABEL[row.subjectType]}</div>
                  <div className="font-mono text-[10px] text-gray-500">{row.subjectId}</div>
                </td>
                <td className="px-4 py-2"><DocumentStatusChip status={row.status} /></td>
                <td className="px-4 py-2 text-gray-700 text-xs">{formatShortDate(row.issuedAt)}</td>
                <td className="px-4 py-2 text-gray-700 text-xs">{formatShortDate(row.expiresAt)}</td>
                <td className="px-4 py-2 text-gray-700 text-xs">{row.owner?.fullName ?? row.owner?.email ?? '—'}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => setSelected(row)}
                    className="text-xs text-indigo-700 hover:underline"
                    data-testid={`compliance-doc-view-${row.id}`}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="p-6 text-center text-sm text-gray-500">No documents match your filters.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {creating && (
        <DocumentCreateDialog onClose={() => setCreating(false)} onError={setBanner} />
      )}

      {selected && (
        <DocumentDetailDialog
          document={selected}
          canUpload={canUpload}
          onClose={() => setSelected(null)}
          onError={setBanner}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// DocumentCreateDialog — metadata-only (no upload today)
// ---------------------------------------------------------------------------

function DocumentCreateDialog({
  onClose,
  onError,
}: {
  onClose: () => void;
  onError: (msg: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [requirementId, setRequirementId] = useState('');
  const [subjectType, setSubjectType] = useState<ComplianceSubjectType>('provider_user');
  const [subjectId, setSubjectId] = useState('');
  const [effectiveAt, setEffectiveAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const { data: reqs } = useGetComplianceRequirementsQuery({ activeOnly: true, limit: 500 });
  const [create, state] = useCreateComplianceDocumentMutation();

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    try {
      const created = await create({
        title: title.trim(),
        requirementId,
        subjectType,
        subjectId: subjectId.trim(),
        effectiveAt: effectiveAt ? new Date(effectiveAt).toISOString() : null,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      }).unwrap();
      // Alert operator if the backend confirms Central Documents is off.
      if (created.centralDocumentsEnabled === false) {
        onError(
          'Metadata created. Document upload is not available until the Central Document Platform is enabled.',
        );
      }
      onClose();
    } catch (e) {
      setErr(messageFromComplianceError(e));
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="compliance-doc-dialog-title"
    >
      <form onSubmit={handle} className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl space-y-4" data-testid="compliance-doc-form">
        <h2 id="compliance-doc-dialog-title" className="text-lg font-semibold text-gray-900">Create evidence metadata</h2>
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          You're creating a metadata record only. The file itself cannot be uploaded yet — Central Document Platform is not enabled.
        </div>

        {err && (
          <div role="alert" className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {err}
          </div>
        )}

        <div>
          <label htmlFor="doc-title" className="block text-xs font-medium text-gray-700 mb-1">Title</label>
          <input id="doc-title" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full rounded border-gray-300 text-sm" required />
        </div>
        <div>
          <label htmlFor="doc-req" className="block text-xs font-medium text-gray-700 mb-1">Requirement</label>
          <select id="doc-req" value={requirementId} onChange={(e) => setRequirementId(e.target.value)} className="w-full rounded border-gray-300 text-sm" required>
            <option value="" disabled>Choose…</option>
            {(reqs?.rows ?? []).map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="doc-subject-type" className="block text-xs font-medium text-gray-700 mb-1">Subject type</label>
            <select id="doc-subject-type" value={subjectType} onChange={(e) => setSubjectType(e.target.value as ComplianceSubjectType)} className="w-full rounded border-gray-300 text-sm">
              {(Object.entries(SUBJECT_TYPE_LABEL) as Array<[ComplianceSubjectType, string]>).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="doc-subject-id" className="block text-xs font-medium text-gray-700 mb-1">Subject id</label>
            <input id="doc-subject-id" value={subjectId} onChange={(e) => setSubjectId(e.target.value)} className="w-full rounded border-gray-300 text-sm font-mono" required />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="doc-effective" className="block text-xs font-medium text-gray-700 mb-1">Effective date (optional)</label>
            <input id="doc-effective" type="date" value={effectiveAt} onChange={(e) => setEffectiveAt(e.target.value)} className="w-full rounded border-gray-300 text-sm" />
          </div>
          <div>
            <label htmlFor="doc-expires" className="block text-xs font-medium text-gray-700 mb-1">Expiry date (optional)</label>
            <input id="doc-expires" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="w-full rounded border-gray-300 text-sm" />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={state.isLoading}>
            {state.isLoading ? 'Saving…' : 'Create metadata'}
          </Button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DocumentDetailDialog — view + submit/archive/upload (upload always 503)
// ---------------------------------------------------------------------------

function DocumentDetailDialog({
  document,
  canUpload,
  onClose,
  onError,
}: {
  document: ComplianceDocument;
  canUpload: boolean;
  onClose: () => void;
  onError: (msg: string) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [showRenew, setShowRenew] = useState(false);
  const [upload, uploadState] = useUploadComplianceDocumentMutation();
  const [submit, submitState] = useSubmitComplianceDocumentMutation();
  const [archive, archiveState] = useArchiveComplianceDocumentMutation();

  const showPendingBanner = document.status === 'pending_upload';

  // Renewal eligibility mirrors the backend service (`renewComplianceDocument`):
  //   allowed origins are active / expiring / expired.
  // Anything else hides the button — Central-Documents-blocked
  // renewals still surface the 503 business copy via
  // `messageFromComplianceError` when the mutation returns.
  const canRenew =
    document.status === 'active' ||
    document.status === 'expiring' ||
    document.status === 'expired';

  const handleUpload = async () => {
    if (!file) return;
    try {
      await upload({ id: document.id, file }).unwrap();
    } catch (e) {
      // Surface the exact backend copy for the 503 case.
      onError(messageFromComplianceError(e));
    }
  };

  const handleSubmit = async () => {
    try {
      await submit(document.id).unwrap();
      onClose();
    } catch (e) {
      onError(messageFromComplianceError(e));
    }
  };

  const handleArchive = async () => {
    try {
      await archive(document.id).unwrap();
      onClose();
    } catch (e) {
      onError(messageFromComplianceError(e));
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="compliance-doc-detail-title"
    >
      <div className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl space-y-4 max-h-[90vh] overflow-y-auto" data-testid="compliance-doc-detail">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id="compliance-doc-detail-title" className="text-lg font-semibold text-gray-900">{document.title}</h2>
            <div className="text-xs text-gray-500 mt-1">{document.requirement?.name ?? 'Unknown requirement'}</div>
          </div>
          <DocumentStatusChip status={document.status} />
        </div>

        {showPendingBanner && (
          <div
            role="alert"
            className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            data-testid="compliance-doc-detail-upload-disabled"
          >
            The Central Document Platform is not enabled. Evidence upload is disabled for this record.
          </div>
        )}

        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-xs text-gray-500">Subject</dt>
            <dd className="text-gray-900">{SUBJECT_TYPE_LABEL[document.subjectType]}</dd>
            <dd className="text-xs text-gray-500 font-mono">{document.subjectId}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Owner</dt>
            <dd className="text-gray-900">{document.owner?.fullName ?? document.owner?.email ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Issued</dt>
            <dd className="text-gray-900">{formatShortDate(document.issuedAt)}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Expires</dt>
            <dd className="text-gray-900">{formatShortDate(document.expiresAt)}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Approved</dt>
            <dd className="text-gray-900">{formatShortDate(document.approvedAt)}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Central document id</dt>
            <dd className="text-gray-900 font-mono text-xs">{document.currentCentralDocumentId ?? '—'}</dd>
          </div>
        </dl>

        {canUpload && (
          <div className="border-t border-gray-100 pt-3 space-y-2">
            <label htmlFor="doc-upload-file" className="block text-xs font-medium text-gray-700">Upload evidence (currently disabled)</label>
            <input
              id="doc-upload-file"
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="text-sm"
              data-testid="compliance-doc-file-input"
            />
            <Button
              type="button"
              onClick={handleUpload}
              disabled={!file || uploadState.isLoading}
              data-testid="compliance-doc-upload-btn"
            >
              {uploadState.isLoading ? 'Uploading…' : 'Upload'}
            </Button>
            <p className="text-xs text-gray-500">
              This button will return a 503 until the Central Document Platform is enabled. We do not fall back to local storage — the file will not be persisted.
            </p>
          </div>
        )}

        <div className="border-t border-gray-100 pt-3 flex flex-wrap gap-2">
          {document.status === 'draft' && (
            <Button type="button" variant="secondary" onClick={handleSubmit} disabled={submitState.isLoading} data-testid="compliance-doc-submit-btn">
              {submitState.isLoading ? 'Submitting…' : 'Submit for approval'}
            </Button>
          )}
          {canUpload && canRenew && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowRenew(true)}
              data-testid="compliance-doc-renew-btn"
            >
              Renew
            </Button>
          )}
          {document.status !== 'archived' && (
            <Button type="button" variant="secondary" onClick={handleArchive} disabled={archiveState.isLoading} data-testid="compliance-doc-archive-btn">
              {archiveState.isLoading ? 'Archiving…' : 'Archive'}
            </Button>
          )}
          <div className="grow" />
          <Button type="button" variant="ghost" onClick={onClose}>Close</Button>
        </div>
      </div>

      {showRenew && (
        <RenewDocumentDialog
          document={document}
          onClose={() => setShowRenew(false)}
          onCompleted={onClose}
          onError={onError}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RenewDocumentDialog
// ---------------------------------------------------------------------------
//
// Mints a fresh predecessor→successor pair via the existing renew
// mutation. Backend transitions the predecessor to `renewed` and
// creates the successor in `pending_upload`.  If Central Documents is
// unreachable during the renewal chain, the backend still returns the
// records — the successor simply starts in pending_upload and the
// tab-level banner remains visible. Any true 503 with
// `central_documents_unavailable` surfaces the shared business copy.
//
// The mutation itself owns tag invalidation (both predecessor + successor),
// so this dialog doesn't need to trigger a refetch manually.

function RenewDocumentDialog({
  document,
  onClose,
  onCompleted,
  onError,
}: {
  document: ComplianceDocument;
  onClose: () => void;
  onCompleted: () => void;
  onError: (msg: string) => void;
}) {
  const [effectiveAt, setEffectiveAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [renew, state] = useRenewComplianceDocumentMutation();

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    try {
      await renew({
        id: document.id,
        effectiveAt: effectiveAt ? new Date(effectiveAt).toISOString() : null,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        notes: notes.trim() || null,
      }).unwrap();
      onCompleted();
    } catch (e2) {
      const msg = messageFromComplianceError(e2);
      setErr(msg);
      onError(msg);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="compliance-doc-renew-title"
    >
      <form
        onSubmit={handle}
        className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl space-y-3"
        data-testid="compliance-doc-renew-dialog"
      >
        <h3 id="compliance-doc-renew-title" className="text-lg font-semibold text-gray-900">
          Renew "{document.title}"
        </h3>
        <p className="text-xs text-gray-500">
          Marks the current document <em>renewed</em> and creates a fresh predecessor→successor
          pair. The successor starts in <em>pending upload</em> — evidence cannot be attached until
          Central Documents is enabled.
        </p>
        {err && (
          <div role="alert" className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {err}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="renew-effective" className="block text-xs font-medium text-gray-700 mb-1">
              Effective date (optional)
            </label>
            <input
              id="renew-effective"
              type="date"
              value={effectiveAt}
              onChange={(e) => setEffectiveAt(e.target.value)}
              className="w-full rounded border-gray-300 text-sm"
            />
          </div>
          <div>
            <label htmlFor="renew-expires" className="block text-xs font-medium text-gray-700 mb-1">
              Expires (optional)
            </label>
            <input
              id="renew-expires"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full rounded border-gray-300 text-sm"
            />
          </div>
        </div>
        <div>
          <label htmlFor="renew-notes" className="block text-xs font-medium text-gray-700 mb-1">
            Notes (optional)
          </label>
          <textarea
            id="renew-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full rounded border-gray-300 text-sm"
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose} disabled={state.isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={state.isLoading}
            data-testid="compliance-doc-renew-confirm"
          >
            {state.isLoading ? 'Renewing…' : 'Renew'}
          </Button>
        </div>
      </form>
    </div>
  );
}
